<template>
    <div class="page container p-4 py-4">
      <section class="login-hero text-center mb-4" v-if="!hasLastSession">
        <span class="section-eyebrow">Acesse sua conta</span>
        <h2 class="mb-2 text-primary-emphasis">Entrar na sua conta</h2>
        <p class="text-muted mb-0 description-text">
          Bem-vindo(a) de volta! Entre para acompanhar seus eventos e recebimentos.
        </p>
      </section>

      <section v-if="hasLastSession" class="last-session-hero text-center mb-4">
        <div class="last-session-avatar mx-auto">
          <img v-if="lastSession.avatar_url" :src="lastSession.avatar_url" class="last-session-avatar__img" alt="Avatar" referrerpolicy="no-referrer" />
          <template v-else>{{ lastSessionInitial }}</template>
        </div>
        <h3 class="mt-3 mb-0 fw-bold text-primary-emphasis">Olá, {{ lastSessionFirstName }}</h3>
        <p v-if="lastSession.email" class="text-muted small mt-1 mb-0">{{ lastSession.email }}</p>
      </section>

      <section class="mb-4">
        <!-- MODO SESSÃO SALVA -->
        <div v-if="hasLastSession" class="remembered-form">

          <!-- PRIMARY: Senha -->
          <form v-if="lastSession.method === 'password'" @submit.prevent="login" class="register-form">
            <div class="mb-3">
              <label for="password" class="field-label">Sua senha</label>
              <div class="password-field-wrapper">
                <input
                  v-model="form.password"
                  placeholder="Preencha sua senha"
                  :type="passwordFieldType"
                  id="password"
                  :class="['form-control register-input', { 'is-invalid': errors.password }]"
                  ref="passwordInputSession"
                />
                <button type="button" class="password-toggle-btn" tabindex="-1" @click="togglePasswordVisibility">
                  <i :class="passwordToggleIcon"></i>
                </button>
              </div>
              <div v-if="errors.password" class="field-error">{{ errors.password }}</div>
            </div>
            <div class="d-grid mt-3">
              <button type="submit" :disabled="loading" class="btn btn-color-site btn-lg register-btn">
                <span v-if="loading"><i class="fas fa-spinner fa-spin me-2"></i> Entrando...</span>
                <span v-else>Entrar</span>
              </button>
            </div>
          </form>

          <!-- PRIMARY: Google -->
          <div v-else-if="lastSession.method === 'google'" class="d-grid">
            <button type="button" class="btn btn-google-fallback w-100" @click="openGooglePopup" :disabled="loading">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" width="18" height="18" alt="" />
              Continuar com Google
            </button>
          </div>

          <!-- PRIMARY: Facebook -->
          <div v-else-if="lastSession.method === 'facebook'" class="d-grid">
            <button type="button" class="btn btn-facebook w-100" @click="openFacebookPopup" :disabled="loading">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.592 1.323-1.324V1.324C24 .593 23.408 0 22.676 0"/></svg>
              Continuar com Facebook
            </button>
          </div>

          <div class="social-divider my-3"><span>ou</span></div>

          <!-- SECUNDÁRIAS quando primary = senha -->
          <template v-if="lastSession.method === 'password'">
            <div class="d-grid mb-2">
              <button type="button" class="btn btn-google-fallback w-100" @click="openGooglePopup" :disabled="loading">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" width="18" height="18" alt="" />
                Entrar com Google
              </button>
            </div>
            <div class="d-grid">
              <button type="button" class="btn btn-facebook w-100" @click="openFacebookPopup" :disabled="loading">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.592 1.323-1.324V1.324C24 .593 23.408 0 22.676 0"/></svg>
                Entrar com Facebook
              </button>
            </div>
          </template>

          <!-- SECUNDÁRIAS quando primary = google -->
          <template v-else-if="lastSession.method === 'google'">
            <div class="d-grid mb-2">
              <button type="button" class="btn btn-alt-email w-100" @click="clearLastSession">
                <i class="fas fa-envelope"></i> Entrar com email e senha
              </button>
            </div>
            <div class="d-grid">
              <button type="button" class="btn btn-facebook w-100" @click="openFacebookPopup" :disabled="loading">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.592 1.323-1.324V1.324C24 .593 23.408 0 22.676 0"/></svg>
                Entrar com Facebook
              </button>
            </div>
          </template>

          <!-- SECUNDÁRIAS quando primary = facebook -->
          <template v-else-if="lastSession.method === 'facebook'">
            <div class="d-grid mb-2">
              <button type="button" class="btn btn-google-fallback w-100" @click="openGooglePopup" :disabled="loading">
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" width="18" height="18" alt="" />
                Entrar com Google
              </button>
            </div>
            <div class="d-grid">
              <button type="button" class="btn btn-alt-email w-100" @click="clearLastSession">
                <i class="fas fa-envelope"></i> Entrar com email e senha
              </button>
            </div>
          </template>

          <div class="d-flex flex-column gap-2 mt-4 login-links">
            <a href="#" @click.prevent="clearLastSession" class="text-link">Não sou eu</a>
            <router-link to="/forgot-password" class="text-link">Esqueci minha senha</router-link>
          </div>
        </div>

        <!-- MODO PADRÃO -->
        <form v-else @submit.prevent="login" class="register-form">
          <div class="mb-3">
            <label for="email" class="field-label">E-mail</label>
            <input
              v-model="form.email"
              placeholder="Preencha seu e-mail"
              type="email"
              id="email"
              :class="['form-control register-input', { 'is-invalid': errors.email }]"
              ref="emailInput"
            />
            <div v-if="errors.email" class="field-error">{{ errors.email }}</div>
          </div>

          <div class="mb-3">
            <label for="password" class="field-label">Senha</label>
            <div class="password-field-wrapper">
              <input
                v-model="form.password"
                placeholder="Preencha sua senha"
                :type="passwordFieldType" id="password"
                :class="['form-control register-input', { 'is-invalid': errors.password }]"
                ref="passwordInput"
              />
              <button
                type="button"
                class="password-toggle-btn"
                tabindex="-1"
                @click="togglePasswordVisibility"
              >
                <i :class="passwordToggleIcon"></i>
              </button>
            </div>
            <div v-if="errors.password" class="field-error">{{ errors.password }}</div>
          </div>

          <div class="d-grid mt-4">
            <button type="submit" :disabled="loading" class="btn btn-color-site btn-lg register-btn">
              <span v-if="loading">
                <i class="fas fa-spinner fa-spin me-2"></i> Entrando...
              </span>
              <span v-else>
                Entrar
              </span>
            </button>
          </div>

          <div class="social-divider my-3">
            <span>ou</span>
          </div>

          <div class="d-grid">
            <div v-show="googleBtnRendered" id="google-signin-btn" class="google-btn-wrapper"></div>
            <button
              v-if="!googleBtnRendered"
              type="button"
              class="btn btn-google-fallback w-100"
              @click="openGooglePopup"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" width="18" height="18" alt="" class="me-2" />
              Entrar com Google
            </button>
          </div>

          <div class="d-grid mt-2">
            <button
              type="button"
              class="btn btn-facebook w-100"
              @click="openFacebookPopup"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="white" class="me-2"><path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.592 1.323-1.324V1.324C24 .593 23.408 0 22.676 0"/></svg>
              Entrar com Facebook
            </button>
          </div>

          <div class="d-flex flex-column gap-2 mt-4 login-links">
            <router-link to="/register" class="text-link">Não tem uma conta? <b>Cadastre aqui</b></router-link>
            <router-link to="/forgot-password" class="text-link">Esqueci minha senha</router-link>
          </div>
        </form>
      </section>

      <BottomModal 
        :visible="showConfirmationModal" 
        @close="closeConfirmationModal"
        :fullHeight="false"
      >
        <template #default>
          <div class="confirmation-content p-3 text-center">
            <h5 class="text-center mb-3 text-primary-emphasis">Confirmar Envio do Código</h5>
            <p class="text-muted mb-4">
              Um código de 4 dígitos será enviado para o seguinte {{ isSendingToPhone ? 'celular' : 'e-mail' }}.
              <br>Confirme abaixo:
            </p>
            
            <div class="summary-item mb-3 p-2 border rounded bg-light">
              <span class="fw-bold">{{ isSendingToPhone ? 'Celular:' : 'E-mail:' }}</span> 
              <span class="text-primary-emphasis">{{ contactForCode }}</span>
            </div>

            <div class="d-flex gap-3 mt-4"> 
              <button class="btn btn-outline-secondary flex-fill" @click="closeConfirmationModal">
                  <i class="fas fa-times me-2"></i> Fechar
              </button>
              <button 
                class="btn btn-color-site flex-fill" 
                @click="sendResetCode" 
                :disabled="loadingSendCode"
              >
                  <span v-if="loadingSendCode" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {{ loadingSendCode ? 'Enviando...' : 'Confirmar e Enviar Código' }}
              </button>
            </div>
          </div>
        </template>
      </BottomModal>

      <BottomModal 
        :visible="showCodeInputModal" 
        @close="closeCodeInputModal"
        :fullHeight="false"
      >
        <template #default>
            <div class="code-input-content p-3 text-center">
                <h5 class="text-center mb-3 text-primary-emphasis">Verificar Código</h5>
                <p class="text-muted mb-4">
                    Digite o código de 4 dígitos que foi enviado para o seu {{ isSendingToPhone ? 'celular' : 'e-mail' }}.
                </p>

                <form @submit.prevent="verifyCode">
                    <div class="d-flex justify-content-center gap-2 mb-4">
                        <input
                            v-for="(digit, index) in code"
                            :key="index"
                            ref="codeInput"
                            v-model="code[index]"
                            @input="handleInput($event, index)"
                            @keydown.delete="handleDelete($event, index)"
                            @paste="handlePaste"
                            type="tel"
                            class="form-control code-input text-center"
                            maxlength="1"
                            inputmode="numeric"
                            autocomplete="one-time-code"
                            required
                        />
                    </div>

                    <div class="d-flex flex-column gap-2 mt-4">
                        <button type="submit" class="btn btn-color-site btn-lg" :disabled="!isCodeComplete || loadingVerifyCode">
                            <span v-if="loadingVerifyCode" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {{ loadingVerifyCode ? 'Verificando...' : 'Validar Código' }}
                        </button>
                        
                        <div class="d-flex justify-content-between align-items-center mt-2">
                           <a href="#" class="text-link" @click.prevent="closeCodeInputModal">Cancelar</a>

                           <button type="button" class="btn btn-link text-link p-0" @click.prevent="resendCode" :disabled="isResendDisabled">
                                Reenviar Código 
                                <span v-if="countdown > 0">({{ countdown }}s)</span>
                           </button>
                        </div>
                    </div>
                </form>
            </div>
        </template>
      </BottomModal>

      <BottomModal 
        :visible="showSuccessModal" 
        @close="redirectToResetPassword"
        :fullHeight="true"
      >
        <template #default>
            <div class="success-content p-3 text-center d-flex flex-column justify-content-center h-100">
                <i class="fas fa-check-circle text-success mb-4" style="font-size: 5rem;"></i>
                <h5 class="text-center mb-3 text-primary-emphasis">Código Verificado!</h5>
                <p class="text-muted mb-4">
                    Seu código foi validado com sucesso. Você será redirecionado para criar uma nova senha.
                </p>
                <div class="d-flex gap-3 mt-4"> 
                    <button class="btn btn-color-site flex-fill" @click="redirectToResetPassword">
                        Criar Nova Senha ({{ redirectCountdown }}s)
                    </button>
                </div>
            </div>
        </template>
      </BottomModal>
    </div>
</template>

<script>
import { useAuthStore } from '@/stores/authStore';
import { parseError } from '@/utils/errorHandler';
import api from '@/services/api';
import { toast } from 'vue3-toastify';
import BottomModal from '@/components/BottomModal.vue';

const LOCAL_STORAGE_KEY = 'forgotPasswordState';

export default {
  name: 'Login',
  components: {
    BottomModal
  },
  data() {
    return {
      form: {
        email: '',
        password: ''
      },
      errors: {},
      loading: false,
      passwordVisible: false,
      googleBtnRendered: false,
      googleNonce: Math.random().toString(36).slice(2) + Date.now().toString(36),
      lastSession: null,
      showConfirmationModal: false,
      showCodeInputModal: false,
      showSuccessModal: false, // NOVO: Estado para o modal de sucesso
      userPhoneNumber: '',
      contactForCode: '',
      isSendingToPhone: false,
      loadingSendCode: false,
      code: ['', '', '', ''],
      loadingVerifyCode: false,
      countdown: 120,
      countdownInterval: null,
      redirectCountdown: 5, // NOVO: Temporizador para redirecionamento
      redirectInterval: null, // NOVO: Intervalo para o redirecionamento
    };
  },
  computed: {
    passwordFieldType() {
      return this.passwordVisible ? 'text' : 'password';
    },
    passwordToggleIcon() {
      return this.passwordVisible ? 'fas fa-eye-slash' : 'fas fa-eye';
    },
    isCodeComplete() {
      return this.code.every(digit => digit.length === 1);
    },
    isResendDisabled() {
      return this.countdown > 0;
    },
    hasLastSession() {
      return Boolean(this.lastSession?.method);
    },
    lastSessionInitial() {
      const n = this.lastSession?.name || this.lastSession?.email || '?';
      return n.charAt(0).toUpperCase();
    },
    lastSessionFirstName() {
      const name = this.lastSession?.name || '';
      return name.split(' ')[0] || this.lastSession?.email || '';
    },
    googleAuthUrl() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId) return '#';
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: `${window.location.origin}/login`,
        response_type: 'id_token',
        scope: 'openid email profile',
        nonce: this.googleNonce,
        prompt: 'select_account',
      });
      return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    },
    facebookAuthUrl() {
      const appId = process.env.VUE_APP_FACEBOOK_APP_ID;
      if (!appId) return '#';
      const params = new URLSearchParams({
        client_id: appId,
        redirect_uri: `${window.location.origin}/login`,
        response_type: 'token',
        scope: 'email,public_profile',
        display: 'popup',
      });
      return `https://www.facebook.com/v19.0/dialog/oauth?${params}`;
    },
  },
  mounted() {
    this.loadStateFromLocalStorage();
    this.loadLastLoginSession();
    this.handleGoogleRedirectCallback();
    this.handleFacebookRedirectCallback();
    this.loadGoogleSignIn();
  },
  beforeUnmount() {
    this.stopCountdown();
    this.stopRedirectCountdown();
    if (this._googleMsgHandler) window.removeEventListener('message', this._googleMsgHandler);
    if (this._googlePopupWatcher) clearInterval(this._googlePopupWatcher);
    if (this._facebookMsgHandler) window.removeEventListener('message', this._facebookMsgHandler);
    if (this._facebookPopupWatcher) clearInterval(this._facebookPopupWatcher);
  },
  methods: {
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    async handleForgotPasswordClick() {
      if (!this.form.email.trim() || !this.isValidEmail(this.form.email)) {
        toast.error('Por favor, preencha um e-mail válido para recuperar a senha.', {
          autoClose: 3000,
          position: 'top-right',
        });
        return;
      }
      this.loading = true;
      this.userPhoneNumber = '';
      this.isSendingToPhone = false;
      try {
        const response = await api.post('/auth/get-phone-by-email', { email: this.form.email }); 
        
        if (response.data && response.data.phone) {
          let fetchedPhone = response.data.phone.replace(/\D/g, '');
          if (fetchedPhone.startsWith('55') && fetchedPhone.length > 2) {
              fetchedPhone = fetchedPhone.substring(2); 
          }
          this.userPhoneNumber = this.applyPhoneMask(fetchedPhone, '(##) #####-####');
          this.contactForCode = this.userPhoneNumber;
          this.isSendingToPhone = true;
        } else {
          this.contactForCode = this.form.email;
          this.isSendingToPhone = false;
        }
        
        this.showConfirmationModal = true;
        this.saveStateToLocalStorage();
      } catch (error) {
        console.error('Erro ao buscar telefone, usando e-mail como fallback:', error);
        this.contactForCode = this.form.email;
        this.isSendingToPhone = false;
        this.showConfirmationModal = true;
        this.saveStateToLocalStorage();
      } finally {
        this.loading = false;
      }
    },
    async sendResetCode() {
      this.loadingSendCode = true;
      try {
        const payload = { email: this.form.email };
        await api.post('/auth/send-reset-code', payload);
        toast.success(`Código de 4 dígitos enviado para o seu ${this.isSendingToPhone ? 'celular' : 'e-mail'}!`, {
          autoClose: 3000,
          position: 'top-right',
        });
        this.showConfirmationModal = false; 
        this.openCodeInputModal();
      } catch (error) {
        const friendlyMessage = parseError(error) || 'Ocorreu um erro ao enviar o código. Tente novamente.';
        toast.error(friendlyMessage, {
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        this.loadingSendCode = false;
      }
    },
    openCodeInputModal() {
        this.showCodeInputModal = true;
        this.saveStateToLocalStorage();
        this.$nextTick(() => {
            if (this.$refs.codeInput && this.$refs.codeInput[0]) {
                this.$refs.codeInput[0].focus();
            }
        });
        this.startCountdown();
    },
    closeCodeInputModal() {
        this.showCodeInputModal = false;
        this.code = ['', '', '', ''];
        this.stopCountdown();
        this.clearStateFromLocalStorage();
    },
    closeConfirmationModal() {
        this.showConfirmationModal = false;
        this.clearStateFromLocalStorage();
    },
    handleInput(event, index) {
      const value = event.target.value;
      this.code[index] = value.replace(/[^0-9]/g, '').substring(0, 1);
      if (this.code[index] && index < 3) {
        this.$refs.codeInput[index + 1].focus();
      }
    },
    handleDelete(event, index) {
      if (event.target.value === '' && index > 0) {
        this.code[index - 1] = '';
        this.$refs.codeInput[index - 1].focus();
      }
    },
    handlePaste(event) {
        event.preventDefault();
        const pasteData = event.clipboardData.getData('text').replace(/[^0-9]/g, '');
        if (pasteData.length >= 4) {
            for (let i = 0; i < 4; i++) {
                this.code[i] = pasteData.charAt(i);
            }
            this.verifyCode();
        } else {
            toast.error('O código colado deve ter 4 dígitos.', { position: 'top-right' });
        }
    },
    async verifyCode() {
        if (!this.isCodeComplete) {
            toast.error('Por favor, preencha todos os 4 dígitos do código.', { position: 'top-right' });
            return;
        }
        this.loadingVerifyCode = true;
        try {
            const fullCode = this.code.join('');
            const payload = {
                email: this.form.email,
                code: fullCode
            };
            const response = await api.post('/auth/verify-reset-code', payload);
            this.closeCodeInputModal();
            this.clearStateFromLocalStorage();
            this.openSuccessModal(); // NOVO: Abre o modal de sucesso
        } catch (error) {
            const friendlyMessage = parseError(error) || 'O código inserido é inválido ou expirou.';
            toast.error(friendlyMessage, {
                autoClose: 3000,
                position: 'top-right',
            });
        } finally {
            this.loadingVerifyCode = false;
        }
    },
    async resendCode() {
        if (this.isResendDisabled) return;
        this.loadingSendCode = true;
        try {
            const payload = { email: this.form.email };
            await api.post('/auth/send-reset-code', payload);
            toast.success(`Um novo código foi enviado para o seu ${this.isSendingToPhone ? 'celular' : 'e-mail'}!`, {
                autoClose: 3000,
                position: 'top-right',
            });
            this.startCountdown();
            this.code = ['', '', '', ''];
        } catch (error) {
            const friendlyMessage = parseError(error) || 'Ocorreu um erro ao reenviar o código. Tente novamente.';
            toast.error(friendlyMessage, {
                autoClose: 3000,
                position: 'top-right',
            });
        } finally {
            this.loadingSendCode = false;
        }
    },
    startCountdown() {
        this.stopCountdown();
        this.countdown = 120;
        this.countdownInterval = setInterval(() => {
            if (this.countdown > 0) {
                this.countdown--;
                this.saveStateToLocalStorage();
            } else {
                this.stopCountdown();
            }
        }, 1000);
    },
    stopCountdown() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    },
    // NOVO: Métodos para o modal de sucesso
    openSuccessModal() {
        this.showSuccessModal = true;
        this.startRedirectCountdown();
    },
    closeSuccessModal() {
        this.showSuccessModal = false;
        this.stopRedirectCountdown();
    },
    startRedirectCountdown() {
        this.stopRedirectCountdown();
        this.redirectCountdown = 5;
        this.redirectInterval = setInterval(() => {
            if (this.redirectCountdown > 0) {
                this.redirectCountdown--;
            } else {
                this.stopRedirectCountdown();
                this.redirectToResetPassword();
            }
        }, 1000);
    },
    stopRedirectCountdown() {
        if (this.redirectInterval) {
            clearInterval(this.redirectInterval);
            this.redirectInterval = null;
        }
    },
    redirectToResetPassword() {
        this.closeSuccessModal();
        this.$router.push('/forgot-password');
    },
    saveStateToLocalStorage() {
        const state = {
            email: this.form.email,
            showConfirmationModal: this.showConfirmationModal,
            showCodeInputModal: this.showCodeInputModal,
            contactForCode: this.contactForCode,
            isSendingToPhone: this.isSendingToPhone,
            countdown: this.countdown,
            code: this.code,
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
    },
    loadStateFromLocalStorage() {
        const storedState = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (storedState) {
            const state = JSON.parse(storedState);
            this.form.email = state.email;
            this.showConfirmationModal = state.showConfirmationModal;
            this.showCodeInputModal = state.showCodeInputModal;
            this.contactForCode = state.contactForCode;
            this.isSendingToPhone = state.isSendingToPhone;
            this.code = state.code;
            this.countdown = state.countdown;
            if (this.showCodeInputModal) {
                this.startCountdown();
            }
        }
    },
    clearStateFromLocalStorage() {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
    },
    saveLastLoginSession(name, email, method, avatarUrl) {
      const data = { name: name || '', email: email || '', method, avatar_url: avatarUrl || '', at: Date.now() };
      localStorage.setItem('lastLoginSession', JSON.stringify(data));
      this.lastSession = data;
    },
    loadLastLoginSession() {
      try {
        const raw = localStorage.getItem('lastLoginSession');
        if (!raw) return;
        const data = JSON.parse(raw);
        if (!data?.method) return;
        this.lastSession = data;
        if (data.method === 'password' && data.email) {
          this.form.email = data.email;
        }
      } catch { /* ignore */ }
    },
    clearLastSession() {
      localStorage.removeItem('lastLoginSession');
      this.lastSession = null;
      this.form.email = '';
      this.form.password = '';
      this.$nextTick(() => {
        if (!this.googleBtnRendered) this.loadGoogleSignIn();
      });
    },
    applyPhoneMask(phoneNumber, mask) {
      let result = '';
      let maskCharIndex = 0;
      let phoneCharIndex = 0;
      while (maskCharIndex < mask.length && phoneCharIndex < phoneNumber.length) {
        if (mask[maskCharIndex] === '#') {
          result += phoneNumber[phoneCharIndex];
          phoneCharIndex++;
        } else {
          result += mask[maskCharIndex];
        }
        maskCharIndex++;
      }
      return result;
    },
    validateLogin() {
      this.errors = {};
      const isSessionForm = this.hasLastSession && this.lastSession?.method === 'password';
      const email = (this.form.email || '').trim();
      const password = (this.form.password || '');

      if (!isSessionForm) {
        if (!email) {
          this.errors.email = 'O e-mail é obrigatório.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.errors.email = 'Formato de e-mail inválido.';
        }
      }

      if (!password) {
        this.errors.password = 'A senha é obrigatória.';
      } else if (password.length < 6) {
        this.errors.password = 'A senha deve ter pelo menos 6 caracteres.';
      }

      if (this.errors.email) {
        this.$nextTick(() => this.$refs.emailInput?.focus());
      } else if (this.errors.password) {
        const pwRef = isSessionForm ? this.$refs.passwordInputSession : this.$refs.passwordInput;
        this.$nextTick(() => pwRef?.focus());
      }

      return Object.keys(this.errors).length === 0;
    },
    async login() {
      if (!this.validateLogin()) return;
      const auth = useAuthStore();
      this.loading = true;
      try {
        const response = await api.post('/auth/login', this.form);
        auth.setTokenAndDecodeUser(response.data.token, response.data);
        this.saveLastLoginSession(auth.user?.name || '', this.form.email, 'password', auth.user?.avatar_url || '');
        this.$router.push(auth.isAdmin ? '/admin/dashboard' : '/my-account/dashboard');
      } catch (error) {
        const friendlyMessage = parseError(error);
        const status = error?.response?.status;
        if (status === 429) {
          this.errors.password = friendlyMessage;
        } else if (status === 401) {
          this.errors.password = friendlyMessage || 'E-mail ou senha incorretos.';
        } else {
          toast.error(friendlyMessage || 'Ocorreu um erro ao tentar fazer login.', {
            autoClose: 3000,
            position: 'top-right',
          });
        }
        this.$nextTick(() => {
          const isSessionForm = this.hasLastSession && this.lastSession?.method === 'password';
          const pwRef = isSessionForm ? this.$refs.passwordInputSession : this.$refs.passwordInput;
          pwRef?.focus();
        });
      } finally {
        this.loading = false;
      }
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
    loadGoogleSignIn() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId) return;

      const renderBtn = () => {
        if (!window.google?.accounts?.id) return;
        const btnEl = document.getElementById('google-signin-btn');
        if (!btnEl) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => this.handleGoogleCredential(response),
        });
        const w = Math.round(Math.min(btnEl.getBoundingClientRect().width, 400)) || 400;
        window.google.accounts.id.renderButton(btnEl, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: w,
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
        });
        // Força o iframe interno a ocupar 100% da largura do container
        const iframe = btnEl.querySelector('iframe');
        if (iframe) { iframe.style.width = '100%'; iframe.style.minWidth = '100%'; }
      };

      const run = () => {
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (window.google?.accounts?.id) {
              renderBtn();
              return;
            }
            // Script already injected by another call (Register page, etc.) — poll until ready
            if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
              const t = setInterval(() => {
                if (window.google?.accounts?.id) { clearInterval(t); renderBtn(); }
              }, 80);
              setTimeout(() => clearInterval(t), 6000);
              return;
            }
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = renderBtn;
            document.head.appendChild(script);
          });
        });
      };

      run();
    },
    async handleGoogleCredential(response) {
      const auth = useAuthStore();
      this.loading = true;
      try {
        const res = await api.post('/auth/google/callback', { id_token: response.credential });
        auth.setTokenAndDecodeUser(res.data.token, res.data);
        this.saveLastLoginSession(auth.user?.name || '', '', 'google', auth.user?.avatar_url || '');
        if (res.data.isNewUser) {
          auth.setOnboardingMode(true);
          this.$router.push('/my-account/profile');
        } else {
          this.$router.push(auth.isAdmin ? '/admin/dashboard' : '/my-account/dashboard');
        }
      } catch (error) {
        toast.error(parseError(error) || 'Erro ao autenticar com o Google.', {
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        this.loading = false;
      }
    },
    handleGoogleRedirectCallback() {
      const inPopup = !!(window.opener && window.opener !== window);
      const hash = window.location.hash;
      const idToken = hash ? new URLSearchParams(hash.slice(1)).get('id_token') : null;
      if (inPopup) {
        // Sempre fecha o popup — com token (sucesso) ou sem token (cancelamento)
        if (idToken) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
          window.opener.postMessage({ type: 'google-auth', idToken }, window.location.origin);
        }
        window.close();
        return;
      }
      // Fallback redirect direto (popup bloqueado pelo browser)
      if (!idToken) return;
      history.replaceState(null, '', window.location.pathname + window.location.search);
      this.handleGoogleCredential({ credential: idToken });
    },
    handleFacebookRedirectCallback() {
      const inPopup = !!(window.opener && window.opener !== window);
      const hash = window.location.hash;
      const accessToken = hash ? new URLSearchParams(hash.slice(1)).get('access_token') : null;
      if (inPopup) {
        // Sempre fecha o popup — com token (sucesso) ou sem token (cancelamento)
        if (accessToken) {
          history.replaceState(null, '', window.location.pathname + window.location.search);
          window.opener.postMessage({ type: 'facebook-auth', accessToken }, window.location.origin);
        }
        window.close();
        return;
      }
      // Fallback redirect direto (popup bloqueado pelo browser)
      if (!accessToken) return;
      history.replaceState(null, '', window.location.pathname + window.location.search);
      this.handleFacebookCredential(accessToken);
    },
    openGooglePopup() {
      const url = this.googleAuthUrl;
      if (!url || url === '#') return;
      // Limpa listener e watcher anteriores (caso o usuário abra o popup múltiplas vezes)
      if (this._googleMsgHandler) {
        window.removeEventListener('message', this._googleMsgHandler);
        this._googleMsgHandler = null;
      }
      if (this._googlePopupWatcher) {
        clearInterval(this._googlePopupWatcher);
        this._googlePopupWatcher = null;
      }
      const w = 500, h = 620;
      const left = Math.round((screen.width - w) / 2);
      const top = Math.round((screen.height - h) / 2);
      const popup = window.open(url, 'google-oauth', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (!popup) {
        // Popup bloqueado — fallback para redirect
        window.location.href = url;
        return;
      }
      const handler = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'google-auth') return;
        clearInterval(this._googlePopupWatcher);
        this._googlePopupWatcher = null;
        window.removeEventListener('message', handler);
        this._googleMsgHandler = null;
        this.handleGoogleCredential({ credential: event.data.idToken });
      };
      window.addEventListener('message', handler);
      this._googleMsgHandler = handler;
      // Detecta fechamento manual do popup e limpa o listener
      this._googlePopupWatcher = setInterval(() => {
        if (popup.closed) {
          clearInterval(this._googlePopupWatcher);
          this._googlePopupWatcher = null;
          window.removeEventListener('message', this._googleMsgHandler);
          this._googleMsgHandler = null;
        }
      }, 500);
    },
    openFacebookPopup() {
      const url = this.facebookAuthUrl;
      if (!url || url === '#') return;
      // Limpa listener e watcher anteriores
      if (this._facebookMsgHandler) {
        window.removeEventListener('message', this._facebookMsgHandler);
        this._facebookMsgHandler = null;
      }
      if (this._facebookPopupWatcher) {
        clearInterval(this._facebookPopupWatcher);
        this._facebookPopupWatcher = null;
      }
      const w = 500, h = 620;
      const left = Math.round((screen.width - w) / 2);
      const top = Math.round((screen.height - h) / 2);
      const popup = window.open(url, 'facebook-oauth', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (!popup) {
        window.location.href = url;
        return;
      }
      const handler = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'facebook-auth') return;
        clearInterval(this._facebookPopupWatcher);
        this._facebookPopupWatcher = null;
        window.removeEventListener('message', handler);
        this._facebookMsgHandler = null;
        this.handleFacebookCredential(event.data.accessToken);
      };
      window.addEventListener('message', handler);
      this._facebookMsgHandler = handler;
      // Detecta fechamento manual do popup e limpa o listener
      this._facebookPopupWatcher = setInterval(() => {
        if (popup.closed) {
          clearInterval(this._facebookPopupWatcher);
          this._facebookPopupWatcher = null;
          window.removeEventListener('message', this._facebookMsgHandler);
          this._facebookMsgHandler = null;
        }
      }, 500);
    },
    async handleFacebookCredential(accessToken) {
      const auth = useAuthStore();
      this.loading = true;
      try {
        const res = await api.post('/auth/facebook/callback', { access_token: accessToken });
        auth.setTokenAndDecodeUser(res.data.token, res.data);
        this.saveLastLoginSession(auth.user?.name || '', '', 'facebook', auth.user?.avatar_url || '');
        if (res.data.isNewUser) {
          auth.setOnboardingMode(true);
          this.$router.push('/my-account/profile');
        } else {
          this.$router.push(auth.isAdmin ? '/admin/dashboard' : '/my-account/dashboard');
        }
      } catch (error) {
        toast.error(parseError(error) || 'Erro ao autenticar com o Facebook.', {
          autoClose: 3000,
          position: 'top-right',
        });
      } finally {
        this.loading = false;
      }
    },
    signInWithGoogleFallback() {
      const clientId = process.env.VUE_APP_GOOGLE_CLIENT_ID;
      if (!clientId) return;
      const nonce = Math.random().toString(36).slice(2) + Date.now().toString(36);
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: `${window.location.origin}/login`,
        response_type: 'id_token',
        scope: 'openid email profile',
        nonce,
        prompt: 'select_account',
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    },
  }
};
</script>

<style scoped>
:root {
  --primary-color: #ff50a3; /* Sua cor rosa principal (do design) */
  --secondary-color: #32355d; /* Sua cor azul escuro (do design) */
  --text-muted-color: #6c757d; /* Cor para texto suave */
  --button-default-color: #F8F9FA; /* Nova cor para o botão de "Entrar" */
  --button-default-text-color: #6c757d; /* Cor do texto do botão de "Entrar" */
  --button-default-border-color: #ced4da; /* Cor da borda do botão de "Entrar" */
}

.text-primary-emphasis {
  color: var(--secondary-color) !important;
}

/* Estilos da página principal que você já definiu */
.page {
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  height: auto;
  width: 600px;
  max-width: 700px;
  margin: 0 auto;
  align-self: center;
}

/* Padding interno equivalente ao step-content-wrapper de Meus Dados */
section.mb-4 {
  padding: 0 1rem;
}

.register-form {
  padding: 0 0.5rem;
}

@media (max-width: 767.98px) {
  .page {    
    width: 100%;
    margin-top: 0rem;
    min-width: inherit;
  }
}

h2 {
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: var(--secondary-color) !important;
  text-align: center;
}

.description-text {
  font-size: 0.95rem;
  line-height: 1.4;
  color: var(--text-muted-color);
  margin-bottom: 1.5rem;
  text-align: center;
}
.field-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.3rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.input-group {
  position: relative;
  display: flex;
  width: 100%;
}

.register-input {
  background-color: #fafafa;
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.register-input::placeholder {
  color: #c0c0c0;
  font-size: 0.88rem;
}

.register-input:focus {
  background-color: #fff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
  outline: none;
  color: #32355d;
}

.input-group .register-input {
  border-right: none;
  border-radius: 0.75rem 0 0 0.75rem !important;
}

.input-group .register-input:focus {
  border-color: #ff50a3;
  box-shadow: none;
  z-index: 1;
}

.input-group:focus-within {
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
  border-radius: 0.75rem;
}

.password-field-wrapper {
  position: relative;
}

.password-field-wrapper .register-input {
  padding-right: 3rem;
}

.register-input.is-invalid {
  border-color: #e0185d !important;
  box-shadow: 0 0 0 2px rgba(224, 24, 93, 0.08);
}

/* Remove o ícone de exclamação nativo do Bootstrap no campo de senha
   para não sobrepor o botão de mostrar/esconder senha */
.password-field-wrapper .register-input.is-invalid {
  background-image: none;
  padding-right: 3rem;
}

.field-error {
  font-size: 0.78rem;
  color: #e0185d;
  margin-top: 0.25rem;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
  color: #aaa;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: color 0.2s;
  z-index: 2;
}

.password-toggle-btn:hover {
  color: #ff50a3;
}

.password-toggle-btn:focus,
.password-toggle-btn:active {
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}
.btn-color-site:hover:not(:disabled) {
  background-color: #e64593;
  border-color: #e64593;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
.btn-color-site:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.register-btn {
  border-radius: 0.875rem;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.85rem;
  letter-spacing: 0.03em;
  background: linear-gradient(135deg, #ff50a3 0%, #e0185d 100%);
  border: none;
  color: #fff;
  transition: opacity 0.2s, transform 0.1s;
}

.register-btn:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.register-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fa-spinner {
  vertical-align: -0.125em;
}

.text-link {
  font-size: 0.9rem;
  color: #ff50a3;
  text-decoration: none;
}

.text-link:hover {
  text-decoration: underline;
}
.text-link b {
  font-weight: 700;
}
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  white-space: nowrap;
}
.btn i.me-2 {
  margin-right: 0.5rem;
}
.confirmation-content h5 {
  font-size: 1.25rem;
  font-weight: 600;
}
.confirmation-content p {
  font-size: 0.95rem;
}
.summary-item {
  font-size: 1rem;
  color: #32355d;
}
.summary-item .fw-bold {
  color: #32355d;
}
.btn.flex-fill {
    flex: 1 1 auto;
}
.code-input {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    font-weight: 600;
    text-align: center;
    border-radius: 0.5rem;
}

.section-eyebrow {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #ff50a3;
  margin-bottom: 0.4rem;
}

.login-links .text-link {
  font-size: 0.95rem;
}

@media (max-width: 767.98px) {
  .text-primary-emphasis {
    font-size: 1.6rem;
  }

  .description-text {
    font-size: 0.92rem;
  }

}

.social-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #adb5bd;
  font-size: 0.85rem;
}
.social-divider::before,
.social-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #dee2e6;
}

.google-btn-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}
.google-btn-wrapper > div,
.google-btn-wrapper iframe {
  width: 100% !important;
  min-width: 100% !important;
}
.btn-google-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #dadce0;
  color: #3c4043;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  gap: 0.5rem;
  transition: background 0.2s, border-color 0.2s;
}
.btn-google-fallback:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #c6c6c6;
}
.btn-facebook {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1877f2;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.6rem 1rem;
  gap: 0.5rem;
  transition: background 0.2s;
}
.btn-facebook:hover:not(:disabled) {
  background: #1464d0;
}

/* Último acesso */
.last-session-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff50a3 0%, #e0185d 100%);
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.last-session-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.remembered-form {
  max-width: 420px;
  margin: 0 auto;
}
.btn-alt-email {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #ced4da;
  color: #495057;
  transition: background 0.2s, border-color 0.2s;
}
.btn-alt-email:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}
</style>